import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShoppingBag, CheckCircle2, Clock, Zap, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2 } from "lucide-react";

export default function DashOverview() {
  const { t } = useI18n();
  const [orders, setOrders] = useState(null);

  useEffect(() => { api.get("/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);

  if (orders === null) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-amber-400" size={36} /></div>;

  const active = orders.filter((o) => ["Payment Confirmed", "Processing", "In Progress"].includes(o.status));
  const completed = orders.filter((o) => o.status === "Completed");
  const pending = orders.filter((o) => ["Pending Payment", "Payment Detected"].includes(o.status));

  const stats = [
    { icon: ShoppingBag, label: t("dash.totalOrders"), value: orders.length, color: "text-amber-400" },
    { icon: Zap, label: t("dash.activeServices"), value: active.length, color: "text-blue-400" },
    { icon: Clock, label: t("dash.pending"), value: pending.length, color: "text-slate-300" },
    { icon: CheckCircle2, label: t("dash.completed"), value: completed.length, color: "text-emerald-400" },
  ];

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-extrabold text-white">{t("dash.overview")}</h1>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s, i) => (
          <div key={i} className="card-lux p-5" data-testid={`dash-stat-${i}`}>
            <s.icon className={`${s.color} mb-3`} size={22} />
            <div className="font-display text-3xl font-extrabold text-white">{s.value}</div>
            <div className="text-xs text-slate-500 mt-1">{s.label}</div>
          </div>
        ))}
      </div>

      <div className="card-lux p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="font-semibold text-white">Recent Orders</h2>
          <Link to="/dashboard/orders" className="text-sm text-amber-400 flex items-center gap-1">View all <ArrowRight size={14} /></Link>
        </div>
        {orders.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-slate-400">No orders yet.</p>
            <Link to="/services" className="gold-btn mt-4 inline-flex text-sm" data-testid="dash-browse-services">Browse Services <ArrowRight size={15} /></Link>
          </div>
        ) : (
          <div className="space-y-3">
            {orders.slice(0, 5).map((o) => (
              <div key={o.id} className="flex items-center justify-between rounded-lg border border-white/5 p-4 hover:border-amber-500/30 transition-colors" data-testid={`dash-order-${o.order_id}`}>
                <div>
                  <div className="font-medium text-white text-sm">{o.service_name}</div>
                  <div className="text-xs text-slate-500 font-mono">{o.order_id} · {o.total} USDT</div>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={o.status} />
                  {o.status === "Pending Payment" && <Link to={`/payment/${o.order_id}`} className="text-xs text-amber-400 font-semibold">Pay now</Link>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
